import { useState, useMemo, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import Swal from 'sweetalert2';
import SASidebar from '@/pages/sa/dashboard/components/SASidebar';
import { platformFees as initialFees, type PlatformFee, feeStatusLabels, feeStatusColors, feeStatusIcons, getPlansAffectedLabel } from '@/mocks/superAdminPlatformFees';
import { additionalCharges as initialCharges, type AdditionalCharge } from '@/mocks/additionalCharges';
import { planDefinitions } from '@/mocks/superAdminPlans';

type LoadingState = 'loading' | 'ready' | 'error';
type TabType = 'fees' | 'charges';

function simulateLoad() {
  return new Promise<void>((resolve, reject) => {
    setTimeout(() => {
      if (Math.random() > 0.96) reject(new Error('Timeout al conectar.'));
      else resolve();
    }, 500 + Math.random() * 300);
  });
}

function showAlert(icon: 'success' | 'error' | 'warning' | 'info', title: string, text?: string) {
  return Swal.fire({ icon, title, text: text ?? '', confirmButtonColor: '#1a1a1a', customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl' } });
}

function showConfirm(title: string, text: string) {
  return Swal.fire({ icon: 'warning', title, text, showCancelButton: true, confirmButtonText: 'Confirmar', cancelButtonText: 'Cancelar', confirmButtonColor: '#1a1a1a', cancelButtonColor: '#e5e5e5', customClass: { popup: 'rounded-2xl text-sm', title: 'text-sm font-bold', confirmButton: 'text-xs font-medium px-4 py-2 rounded-xl', cancelButton: 'text-xs font-medium px-4 py-2 rounded-xl text-foreground-700' } });
}

export default function SuperAdminFeesCharges() {
  const [fees, setFees] = useState<PlatformFee[]>([]);
  const [charges, setCharges] = useState<AdditionalCharge[]>([]);
  const [loadingState, setLoadingState] = useState<LoadingState>('loading');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabType>('fees');
  const [searchTerm, setSearchTerm] = useState('');

  // Fee editing
  const [editingFeeId, setEditingFeeId] = useState<string | null>(null);
  const [editingFee, setEditingFee] = useState<PlatformFee | null>(null);

  // Charge form
  const [chargeFormOpen, setChargeFormOpen] = useState(false);
  const [editingCharge, setEditingCharge] = useState<AdditionalCharge | null>(null);

  const loadData = useCallback(() => {
    setLoadingState('loading'); setErrorMsg(null);
    simulateLoad()
      .then(() => { setFees(initialFees.map(f => ({ ...f }))); setCharges(initialCharges.map(c => ({ ...c }))); setLoadingState('ready'); })
      .catch((err) => { setErrorMsg(err.message || 'Error desconocido'); setLoadingState('error'); });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ─── FEE ACTIONS ───

  const handleToggleFee = (fee: PlatformFee) => {
    showConfirm(
      fee.active ? '¿Desactivar tarifa?' : '¿Activar tarifa?',
      fee.active ? `La "${fee.name}" se desactivará. Las radios que ya pagaron mantienen vigencia.` : `La "${fee.name}" se activará para ${getPlansAffectedLabel(fee.planIds)}.`,
    ).then((r) => {
      if (r.isConfirmed) {
        setFees(prev => prev.map(f => f.id === fee.id ? { ...f, active: !f.active, updatedAt: new Date().toISOString() } : f));
        showAlert('success', fee.active ? 'Tarifa desactivada' : 'Tarifa activada');
      }
    });
  };

  const handleStartEditFee = (fee: PlatformFee) => {
    setEditingFeeId(fee.id);
    setEditingFee({ ...fee, planIds: [...fee.planIds] });
  };

  const handleSaveFee = () => {
    if (!editingFee) return;
    if (!editingFee.name.trim()) { showAlert('warning', 'Nombre requerido', 'La tarifa debe tener un nombre.'); return; }
    setFees(prev => prev.map(f => f.id === editingFee.id ? { ...editingFee, updatedAt: new Date().toISOString() } : f));
    setEditingFeeId(null); setEditingFee(null);
    showAlert('success', 'Tarifa actualizada');
  };

  const handleToggleFeePlan = (planId: string) => {
    if (!editingFee) return;
    setEditingFee(prev => {
      if (!prev) return null;
      const updated = prev.planIds.includes(planId) ? prev.planIds.filter(id => id !== planId) : [...prev.planIds, planId];
      return { ...prev, planIds: updated };
    });
  };

  const handleCreateFee = () => {
    const newFee: PlatformFee = {
      id: `fee_${Date.now()}`,
      name: 'Nueva Tarifa',
      description: 'Descripción de la nueva tarifa.',
      amount: 0,
      active: false,
      planIds: [],
      gracePeriodDays: 30,
      autoSuspend: true,
      coverageItems: ['Cobertura por definir'],
      suspensionConsequences: ['Suspensión por definir'],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    setFees(prev => [...prev, newFee]);
    setEditingFeeId(newFee.id);
    setEditingFee(newFee);
    showAlert('success', 'Tarifa creada', 'Editá los detalles de la nueva tarifa.');
  };

  const handleDeleteFee = (fee: PlatformFee) => {
    showConfirm('¿Eliminar tarifa?', `La tarifa "${fee.name}" se eliminará permanentemente.`).then((r) => {
      if (r.isConfirmed) {
        setFees(prev => prev.filter(f => f.id !== fee.id));
        showAlert('success', 'Tarifa eliminada', `"${fee.name}" fue eliminada.`);
      }
    });
  };

  // ─── CHARGE ACTIONS ───

  const handleCreateCharge = () => { setEditingCharge(null); setChargeFormOpen(true); };
  const handleEditCharge = (charge: AdditionalCharge) => { setEditingCharge(charge); setChargeFormOpen(true); };

  const handleSaveCharge = (charge: AdditionalCharge) => {
    setCharges(prev => {
      const idx = prev.findIndex(c => c.id === charge.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = charge; return next; }
      return [...prev, charge];
    });
    setChargeFormOpen(false); setEditingCharge(null);
    showAlert('success', editingCharge ? `${charge.name} actualizado` : `${charge.name} creado`);
  };

  const handleToggleCharge = (charge: AdditionalCharge) => {
    setCharges(prev => prev.map(c => c.id === charge.id ? { ...c, active: !c.active } : c));
    showAlert('info', charge.active ? 'Cargo desactivado' : 'Cargo activado', `${charge.name} ahora está ${charge.active ? 'inactivo' : 'activo'}.`);
  };

  const handleDeleteCharge = (charge: AdditionalCharge) => {
    showConfirm('¿Eliminar cargo?', `"${charge.name}" se eliminará permanentemente.`).then((r) => {
      if (r.isConfirmed) {
        setCharges(prev => prev.filter(c => c.id !== charge.id));
        showAlert('success', 'Cargo eliminado', `"${charge.name}" fue eliminado.`);
      }
    });
  };

  // ─── FILTERING ───

  const filteredFees = useMemo(() => {
    if (!searchTerm.trim()) return fees;
    const q = searchTerm.toLowerCase();
    return fees.filter(f => f.name.toLowerCase().includes(q) || f.description.toLowerCase().includes(q));
  }, [fees, searchTerm]);

  const filteredCharges = useMemo(() => {
    if (!searchTerm.trim()) return charges;
    const q = searchTerm.toLowerCase();
    return charges.filter(c => c.name.toLowerCase().includes(q) || c.description.toLowerCase().includes(q));
  }, [charges, searchTerm]);

  const activeFees = fees.filter(f => f.active).length;
  const activeCharges = charges.filter(c => c.active).length;

  return (
    <div className="min-h-screen bg-background-100">
      <div className="flex">
        <SASidebar />
        <main className="flex-1 min-w-0">
          <div className="p-4 md:p-6 lg:p-8 pb-8 space-y-5 max-w-[1600px] mx-auto">
            {/* Breadcrumb */}
            <div className="flex items-center gap-1.5 text-[10px] text-foreground-400">
              <Link to="/sa/dashboard" className="hover:text-foreground-600 transition-colors cursor-pointer">Dashboard</Link>
              <i className="ri-arrow-right-s-line text-[9px]"></i>
              <span className="text-foreground-500 font-medium">Fees & Charges</span>
            </div>

            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
              <div>
                <h2 className="text-foreground-950 font-bold text-base">Fees & Charges</h2>
                <p className="text-foreground-400 text-xs mt-0.5">
                  {loadingState === 'ready' ? `${fees.length} tarifas · ${charges.length} cargos · ${activeFees + activeCharges} activos` : 'Cargando...'}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <div className="relative w-full sm:w-52">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs"></i>
                  <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} placeholder="Buscar..."
                    disabled={loadingState !== 'ready'}
                    className="w-full pl-8 pr-8 py-2 bg-background-50 border border-background-200 rounded-xl text-xs text-foreground-800 placeholder:text-foreground-300 outline-none focus:border-foreground-300 transition-colors disabled:opacity-50" />
                  {searchTerm && <button onClick={() => setSearchTerm('')} className="absolute right-2 top-1/2 -translate-y-1/2 w-5 h-5 rounded-md bg-background-200 flex items-center justify-center cursor-pointer hover:bg-background-300 transition-colors"><i className="ri-close-line text-foreground-500 text-[10px]"></i></button>}
                </div>
                {activeTab === 'fees' && (
                  <button onClick={handleCreateFee} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line text-sm"></i>Nueva Tarifa
                  </button>
                )}
                {activeTab === 'charges' && (
                  <button onClick={handleCreateCharge} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-add-line text-sm"></i>Crear Cargo
                  </button>
                )}
              </div>
            </div>

            {/* Loading */}
            {loadingState === 'loading' && (
              <div className="space-y-4">
                <div className="flex gap-2"><div className="w-24 h-8 bg-background-200 rounded-full animate-pulse"></div><div className="w-24 h-8 bg-background-200 rounded-full animate-pulse"></div></div>
                <div className="bg-background-50 border border-background-200 rounded-2xl p-6 space-y-3">
                  {Array.from({ length: 4 }).map((_, i) => (<div key={i} className="flex items-center gap-4 animate-pulse"><div className="w-8 h-8 bg-background-200 rounded-lg"></div><div className="flex-1"><div className="w-48 h-3 bg-background-200 rounded mb-1.5"></div><div className="w-72 h-2 bg-background-200 rounded"></div></div></div>))}
                </div>
              </div>
            )}

            {/* Error */}
            {loadingState === 'error' && (
              <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-red-50 flex items-center justify-center"><i className="ri-error-warning-line text-red-400 text-2xl"></i></div>
                <p className="text-foreground-800 text-sm font-semibold mb-1">Error al cargar datos</p><p className="text-foreground-400 text-xs mb-4">{errorMsg}</p>
                <button onClick={loadData} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"><i className="ri-refresh-line text-sm"></i>Reintentar</button>
              </div>
            )}

            {/* Ready */}
            {loadingState === 'ready' && (
              <>
                {/* Tab Switcher */}
                <div className="flex items-center gap-1 px-1 py-1 bg-background-200 rounded-full w-fit">
                  <button onClick={() => { setActiveTab('fees'); setSearchTerm(''); }} className={`px-4 py-2 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${activeTab === 'fees' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
                    <i className="ri-shield-keyhole-line mr-1"></i>Platform Fees ({fees.length})
                  </button>
                  <button onClick={() => { setActiveTab('charges'); setSearchTerm(''); }} className={`px-4 py-2 rounded-full text-xs font-semibold cursor-pointer transition-all whitespace-nowrap ${activeTab === 'charges' ? 'bg-background-50 text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-700'}`}>
                    <i className="ri-price-tag-3-line mr-1"></i>Additional Charges ({charges.length})
                  </button>
                </div>

                {/* KPI Cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <div className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0"><i className="ri-shield-check-line text-amber-500 text-sm"></i></div>
                    <div><p className="text-foreground-400 text-[10px] font-medium">Tarifas Activas</p><p className="text-foreground-800 text-sm font-bold">{activeFees}</p></div>
                  </div>
                  <div className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><i className="ri-price-tag-3-line text-emerald-500 text-sm"></i></div>
                    <div><p className="text-foreground-400 text-[10px] font-medium">Cargos Activos</p><p className="text-foreground-800 text-sm font-bold">{activeCharges}</p></div>
                  </div>
                  <div className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-foreground-100 flex items-center justify-center shrink-0"><i className="ri-stack-line text-foreground-500 text-sm"></i></div>
                    <div><p className="text-foreground-400 text-[10px] font-medium">Total Tarifas</p><p className="text-foreground-800 text-sm font-bold">{fees.length}</p></div>
                  </div>
                  <div className="bg-background-50 border border-background-200 rounded-xl p-3 flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-foreground-100 flex items-center justify-center shrink-0"><i className="ri-stack-line text-foreground-500 text-sm"></i></div>
                    <div><p className="text-foreground-400 text-[10px] font-medium">Total Cargos</p><p className="text-foreground-800 text-sm font-bold">{charges.length}</p></div>
                  </div>
                </div>

                {/* ─── PLATFORM FEES TAB ─── */}
                {activeTab === 'fees' && (
                  <>
                    {filteredFees.length === 0 ? (
                      <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                        <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-shield-keyhole-line text-foreground-300 text-2xl"></i></div>
                        <p className="text-foreground-800 text-sm font-semibold mb-1">Sin tarifas</p>
                        <button onClick={() => { setSearchTerm(''); handleCreateFee(); }} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"><i className="ri-add-line text-sm"></i>Crear tarifa</button>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {filteredFees.map((fee) => (
                          <div key={fee.id} className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                            {/* Header */}
                            <div className="flex items-center justify-between p-4 border-b border-background-100">
                              <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-xl bg-foreground-950 flex items-center justify-center shrink-0">
                                  <i className="ri-shield-keyhole-line text-background-50 text-sm"></i>
                                </div>
                                <div>
                                  <div className="flex items-center gap-2">
                                    <h4 className="text-foreground-800 text-xs font-bold">{fee.name}</h4>
                                    <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-wider ${fee.active ? 'bg-emerald-50 text-emerald-600' : 'bg-foreground-200 text-foreground-500'}`}>
                                      {fee.active ? 'Activa' : 'Inactiva'}
                                    </span>
                                  </div>
                                  <p className="text-foreground-400 text-[10px]">Creada {fee.createdAt.split('T')[0]} · {getPlansAffectedLabel(fee.planIds)}</p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                <button onClick={() => handleToggleFee(fee)} className={`px-3 py-1.5 rounded-xl text-[10px] font-semibold transition-all cursor-pointer whitespace-nowrap ${fee.active ? 'bg-red-50 text-red-500 hover:bg-red-100' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'}`}>
                                  <i className={`${fee.active ? 'ri-toggle-fill' : 'ri-toggle-line'} mr-1 text-[11px]`}></i>
                                  {fee.active ? 'Desactivar' : 'Activar'}
                                </button>
                                <button onClick={() => handleStartEditFee(fee)} className="px-3 py-1.5 rounded-xl bg-background-100 text-foreground-600 text-[10px] font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap">
                                  <i className="ri-pencil-line mr-1 text-[11px]"></i>Editar
                                </button>
                                <button onClick={() => handleDeleteFee(fee)} className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors" title="Eliminar">
                                  <i className="ri-delete-bin-line text-red-400 text-xs"></i>
                                </button>
                              </div>
                            </div>

                            {/* Editing panel */}
                            {editingFeeId === fee.id && editingFee && (
                              <div className="border-b border-accent-200 bg-accent-50/30 p-4 space-y-4">
                                <div className="flex items-center justify-between">
                                  <p className="text-accent-800 text-[10px] font-bold uppercase tracking-wider">Editando configuración</p>
                                  <button onClick={() => { setEditingFeeId(null); setEditingFee(null); }} className="w-6 h-6 rounded-md bg-accent-100 flex items-center justify-center cursor-pointer hover:bg-accent-200 transition-colors">
                                    <i className="ri-close-line text-accent-600 text-xs"></i>
                                  </button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <label className="block">
                                    <span className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Nombre</span>
                                    <input type="text" value={editingFee.name} onChange={(e) => setEditingFee(p => p && { ...p, name: e.target.value })} className="w-full mt-1 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300" />
                                  </label>
                                  <label className="block">
                                    <span className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Monto (USD)</span>
                                    <div className="relative mt-1">
                                      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs">$</span>
                                      <input type="number" step="0.01" min="0" value={editingFee.amount} onChange={(e) => setEditingFee(p => p && { ...p, amount: parseFloat(e.target.value) || 0 })} className="w-full pl-6 pr-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300" />
                                    </div>
                                  </label>
                                </div>
                                <label className="block">
                                  <span className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Descripción</span>
                                  <textarea value={editingFee.description} onChange={(e) => setEditingFee(p => p && { ...p, description: e.target.value })} rows={2} className="w-full mt-1 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 resize-y"></textarea>
                                </label>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                  <label className="block">
                                    <span className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Período de Gracia (días)</span>
                                    <input type="number" min="0" max="365" value={editingFee.gracePeriodDays} onChange={(e) => setEditingFee(p => p && { ...p, gracePeriodDays: parseInt(e.target.value) || 0 })} className="w-full mt-1 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300" />
                                  </label>
                                  <div className="flex items-center gap-3 mt-1">
                                    <button type="button" onClick={() => setEditingFee(p => p && { ...p, autoSuspend: !p.autoSuspend })} className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer ${editingFee.autoSuspend ? 'bg-red-400' : 'bg-background-300'}`}>
                                      <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${editingFee.autoSuspend ? 'left-4' : 'left-0.5'}`}></div>
                                    </button>
                                    <span className="text-foreground-700 text-xs">Suspensión automática al vencer gracia</span>
                                  </div>
                                </div>
                                <div>
                                  <span className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Aplicar a Planes</span>
                                  <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                    {planDefinitions.filter(p => p.active).map((plan) => (
                                      <button key={plan.id} onClick={() => handleToggleFeePlan(plan.id)} className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[9px] font-semibold transition-all cursor-pointer whitespace-nowrap ${editingFee.planIds.includes(plan.id) ? 'bg-foreground-950 text-background-50' : 'bg-background-200 text-foreground-500 hover:text-foreground-700'}`}>
                                        <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: plan.color }}></span>{plan.name}
                                      </button>
                                    ))}
                                  </div>
                                </div>
                                <div className="flex justify-end gap-2 pt-2">
                                  <button onClick={() => { setEditingFeeId(null); setEditingFee(null); }} className="px-4 py-2 rounded-xl bg-background-200 text-foreground-600 text-xs font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
                                  <button onClick={handleSaveFee} className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-check-line mr-1 text-[11px]"></i>Guardar</button>
                                </div>
                              </div>
                            )}

                            {/* Stats row */}
                            <div className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
                              <div>
                                <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Monto</p>
                                <p className="text-foreground-800 text-sm font-bold">${fee.amount.toFixed(2)} USD</p>
                                <p className="text-foreground-400 text-[9px]">por año</p>
                              </div>
                              <div>
                                <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Período de Gracia</p>
                                <p className="text-foreground-800 text-sm font-bold">{fee.gracePeriodDays} días</p>
                                <p className="text-foreground-400 text-[9px]">antes de suspensión</p>
                              </div>
                              <div>
                                <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Suspensión Auto</p>
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold ${fee.autoSuspend ? 'bg-red-50 text-red-500' : 'bg-emerald-50 text-emerald-600'}`}>
                                  <i className={`${fee.autoSuspend ? 'ri-alert-line' : 'ri-check-line'} text-[9px]`}></i>
                                  {fee.autoSuspend ? 'Activada' : 'Desactivada'}
                                </span>
                              </div>
                              <div>
                                <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Planes</p>
                                <div className="flex items-center gap-1 flex-wrap">
                                  {fee.planIds.length >= 6 ? (
                                    <span className="text-foreground-500 text-[10px] font-medium">Todos los planes</span>
                                  ) : fee.planIds.map(pid => {
                                    const plan = planDefinitions.find(p => p.id === pid);
                                    return plan ? (
                                      <span key={pid} className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-medium" style={{ backgroundColor: plan.color + '18', color: plan.color }}>
                                        <span className="w-1 h-1 rounded-full" style={{ backgroundColor: plan.color }}></span>{plan.name}
                                      </span>
                                    ) : null;
                                  })}
                                </div>
                              </div>
                            </div>

                            {/* Coverage */}
                            {fee.coverageItems.length > 0 && (
                              <div className="border-t border-background-100 p-4">
                                <p className="text-foreground-500 text-[9px] font-semibold uppercase tracking-wider mb-2">Cobertura</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                  {fee.coverageItems.map((item, idx) => (
                                    <div key={idx} className="flex items-start gap-2"><i className="ri-check-fill text-emerald-400 text-[9px] mt-0.5 shrink-0"></i><span className="text-foreground-600 text-[10px] leading-relaxed">{item}</span></div>
                                  ))}
                                </div>
                              </div>
                            )}

                            {/* Consequences */}
                            {fee.suspensionConsequences.length > 0 && (
                              <div className="border-t border-background-100 p-4 bg-red-50/30">
                                <p className="text-red-500 text-[9px] font-semibold uppercase tracking-wider mb-2">Consecuencias por Falta de Pago</p>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                  {fee.suspensionConsequences.map((item, idx) => (
                                    <div key={idx} className="flex items-start gap-2"><i className="ri-error-warning-line text-red-400 text-[9px] mt-0.5 shrink-0"></i><span className="text-red-600/80 text-[10px] leading-relaxed">{item}</span></div>
                                  ))}
                                </div>
                              </div>
                            )}

                            <div className="border-t border-background-100 p-4">
                              <p className="text-foreground-400 text-[9px] font-semibold uppercase tracking-wider mb-1">Descripción</p>
                              <p className="text-foreground-500 text-[10px] leading-relaxed">{fee.description}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}

                {/* ─── ADDITIONAL CHARGES TAB ─── */}
                {activeTab === 'charges' && (
                  filteredCharges.length === 0 ? (
                    <div className="bg-background-50 border border-background-200 rounded-2xl p-12 text-center">
                      <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-background-100 flex items-center justify-center"><i className="ri-price-tag-3-line text-foreground-300 text-2xl"></i></div>
                      <p className="text-foreground-800 text-sm font-semibold mb-1">Sin cargos adicionales</p>
                      <button onClick={handleCreateCharge} className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer"><i className="ri-add-line text-sm"></i>Crear cargo</button>
                    </div>
                  ) : (
                    <div className="bg-background-50 border border-background-200 rounded-2xl overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[700px]">
                          <thead>
                            <tr className="border-b border-background-200 bg-background-100/50">
                              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Cargo</th>
                              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Descripción</th>
                              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Precio</th>
                              <th className="text-left px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Estado</th>
                              <th className="text-right px-4 py-3 text-foreground-400 text-[10px] font-semibold uppercase tracking-wider">Acciones</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-background-100">
                            {filteredCharges.map((charge) => (
                              <tr key={charge.id} className={`hover:bg-background-100/60 transition-colors ${!charge.active ? 'opacity-55' : ''}`}>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-3 min-w-0">
                                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${charge.active ? 'bg-amber-50 text-amber-600' : 'bg-background-200 text-foreground-400'}`}>
                                      <i className="ri-stack-fill text-sm"></i>
                                    </div>
                                    <p className="text-foreground-800 text-xs font-semibold truncate">{charge.name}</p>
                                  </div>
                                </td>
                                <td className="px-4 py-3 max-w-[300px]"><p className="text-foreground-500 text-[10px] leading-relaxed line-clamp-2">{charge.description}</p></td>
                                <td className="px-4 py-3"><span className="text-foreground-800 text-xs font-bold font-mono">${charge.price.toFixed(2)}</span></td>
                                <td className="px-4 py-3">
                                  <button onClick={() => handleToggleCharge(charge)} className={`relative w-9 h-5 rounded-full transition-colors cursor-pointer ${charge.active ? 'bg-emerald-500' : 'bg-background-300'}`}>
                                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform shadow-sm ${charge.active ? 'translate-x-4.5' : 'translate-x-0.5'}`}></span>
                                  </button>
                                </td>
                                <td className="px-4 py-3">
                                  <div className="flex items-center gap-1 justify-end">
                                    <button onClick={() => handleEditCharge(charge)} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors" title="Editar"><i className="ri-pencil-line text-foreground-500 text-xs"></i></button>
                                    <button onClick={() => handleDeleteCharge(charge)} className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors" title="Eliminar"><i className="ri-delete-bin-line text-red-400 text-xs"></i></button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )
                )}

                {/* Info banner */}
                <div className="bg-accent-50 border border-accent-200 rounded-2xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-xl bg-white flex items-center justify-center shrink-0 mt-0.5"><i className="ri-information-line text-accent-500 text-base"></i></div>
                    <div>
                      <p className="text-accent-800 text-xs font-semibold mb-1">Fees & Charges Consolidado</p>
                      <p className="text-accent-600/80 text-[10px] leading-relaxed">
                        Las tarifas de plataforma (Platform Fees) son independientes de los planes de suscripción. Los cargos adicionales (Additional Charges) son servicios extra que las radios pueden contratar. La configuración de Frecuencia Preferida se administra en el módulo de <Link to="/sa/frequencies" className="underline font-semibold hover:text-accent-800">Frecuencias</Link>.
                      </p>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </main>
      </div>

      {/* Charge Form Modal */}
      {chargeFormOpen && (
        <ChargeFormModal
          charge={editingCharge}
          onClose={() => { setChargeFormOpen(false); setEditingCharge(null); }}
          onSave={handleSaveCharge}
        />
      )}

      <div className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <p className="text-center text-foreground-400 text-[10px] font-medium">Super Admin · Fees & Charges</p>
      </div>
    </div>
  );
}

/* ─── Charge Form Modal Inline ─── */
function ChargeFormModal({ charge, onClose, onSave }: { charge: AdditionalCharge | null; onClose: () => void; onSave: (c: AdditionalCharge) => void }) {
  const [name, setName] = useState(charge?.name ?? '');
  const [description, setDescription] = useState(charge?.description ?? '');
  const [price, setPrice] = useState(charge?.price ?? 0);
  const [active, setActive] = useState(charge?.active ?? true);

  const isEditing = charge !== null;

  const handleSubmit = () => {
    if (!name.trim()) return;
    onSave({
      id: charge?.id ?? `charge_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`,
      name: name.trim(),
      description: description.trim(),
      price,
      active,
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40" onClick={onClose}></div>
      <div className="relative bg-background-50 rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <h3 className="text-foreground-950 font-bold text-sm">{isEditing ? 'Editar Cargo' : 'Nuevo Cargo'}</h3>
          <button onClick={onClose} className="w-7 h-7 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors"><i className="ri-close-line text-foreground-500 text-sm"></i></button>
        </div>
        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Nombre</span>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} className="w-full mt-1 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300" placeholder="Nombre del cargo" />
          </label>
          <label className="block">
            <span className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Descripción</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} maxLength={500} className="w-full mt-1 px-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300 resize-y" placeholder="Descripción del servicio"></textarea>
          </label>
          <div className="grid grid-cols-2 gap-4">
            <label className="block">
              <span className="text-foreground-500 text-[10px] font-semibold uppercase tracking-wider">Precio (USD)</span>
              <div className="relative mt-1"><span className="absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-xs">$</span>
                <input type="number" step="0.01" min="0" value={price} onChange={(e) => setPrice(parseFloat(e.target.value) || 0)} className="w-full pl-6 pr-3 py-2 bg-background-50 border border-background-200 rounded-xl text-xs outline-none focus:border-foreground-300" />
              </div>
            </label>
            <label className="flex items-center gap-3 mt-6">
              <button type="button" onClick={() => setActive(!active)} className={`w-9 h-5 rounded-full transition-colors relative cursor-pointer ${active ? 'bg-emerald-500' : 'bg-background-300'}`}>
                <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow-sm transition-all ${active ? 'left-4' : 'left-0.5'}`}></div>
              </button>
              <span className="text-foreground-700 text-xs">{active ? 'Activo' : 'Inactivo'}</span>
            </label>
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-background-200">
          <button onClick={onClose} className="px-4 py-2 rounded-xl bg-background-200 text-foreground-600 text-xs font-medium hover:bg-background-300 transition-colors cursor-pointer whitespace-nowrap">Cancelar</button>
          <button onClick={handleSubmit} className="px-4 py-2 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"><i className="ri-check-line mr-1 text-[11px]"></i>{isEditing ? 'Guardar' : 'Crear'}</button>
        </div>
      </div>
    </div>
  );
}